import { Spinner, Text, View, XStack, YStack, styled } from 'tamagui'
import { useAudioPlayer, useAudioPlayerStatus } from 'expo-audio'
import { LinearGradient } from 'tamagui/linear-gradient'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { HeroIcon } from '@components/HeroIcon'
import { RangeInput } from '@components/RangeInput'
import { THEME_LIGHT_MODE } from '@constants/features'
import { useHasFeature } from '@hooks/useThemeParams'
import SoundtrackSvg from '@assets/hero/soundtrack.svg'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { PlaybackRateSelector } from '@components/VideoPlayer/PlaybackRateSelector'
import { PLAYBACK_RATE_OPTIONS } from '@components/VideoPlayer/VideoPlayer'
import { IUpdateLessonProgress } from '@components/MediaRender'

interface IAudioPlayerProps {
  url: string
  updateProgress: (params: IUpdateLessonProgress) => void
  actualProgress?: number
  audioStartTime?: number
}

export function AudioPlayer({
  url,
  updateProgress,
  actualProgress = 0,
  audioStartTime,
}: IAudioPlayerProps) {
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  const [showPlaybackRateSelector, setShowPlaybackRateSelector] =
    useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [progressMarkers, setProgressMarkers] = useState({
    25: actualProgress >= 25,
    50: actualProgress >= 50,
    75: actualProgress >= 75,
    100: actualProgress >= 100,
  })
  const player = useAudioPlayer(url, 1000)
  const { playing, duration, currentTime, isBuffering } =
    useAudioPlayerStatus(player)
  const isLightMode = useHasFeature(THEME_LIGHT_MODE)

  const rangeColor = useMemo(() => {
    return isLightMode ? '#7427F1' : '#00E4F5'
  }, [isLightMode])

  const progressThresholds = useMemo(
    () => [
      {
        threshold: 25,
        onProgress: () =>
          updateProgress({
            progress: 25,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 25,
      },
      {
        threshold: 50,
        onProgress: () =>
          updateProgress({
            progress: 50,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 50,
      },
      {
        threshold: 75,
        onProgress: () =>
          updateProgress({
            progress: 75,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 75,
      },
      {
        threshold: 100,
        onProgress: () =>
          updateProgress({
            progress: 100,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 100,
      },
    ],
    [currentTime, updateProgress],
  )

  useEffect(() => {
    const progress = (currentTime / duration) * 100
    progressThresholds.forEach(({ threshold, onProgress, key }) => {
      if (
        !progressMarkers[key as keyof typeof progressMarkers] &&
        progress >= threshold
      ) {
        setProgressMarkers((prev) => ({ ...prev, [key]: true }))
        onProgress?.()
      }
    })
  }, [currentTime, duration, progressMarkers, progressThresholds])

  const handlePlay = useCallback(() => {
    if (playing) {
      player.pause()
    } else {
      if (audioStartTime && !hasStartedPlaying) {
        player.seekTo(audioStartTime)
      }
      setHasStartedPlaying(true)
      player.play()
    }
  }, [player, playing, audioStartTime, hasStartedPlaying])

  const playAndPauseIcon: IconProp = useMemo(() => {
    return playing ? ['fas', 'pause'] : ['fas', 'play']
  }, [playing])

  const formatTime = useMemo(() => {
    const hours = Math.floor(currentTime / 3600)
    const minutes = Math.floor((currentTime % 3600) / 60)
    const seconds = Math.floor(currentTime % 60)

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [currentTime])

  const handleProgressChange = useCallback(
    (value: number) => {
      player.seekTo(value)
    },
    [player],
  )

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      player.setPlaybackRate(rate)
      setPlaybackRate(rate)
    },
    [player, setPlaybackRate],
  )

  return (
    <View
      height="100%"
      width="100%"
      alignItems="center"
      justifyContent="center"
    >
      <SoundtrackSvg
        width="90%"
        height="100%"
        style={{
          top: 40,
        }}
      />
      <LinearGradient
        colors={['transparent', 'rgba(23, 23, 23, 0.6)']}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
        }}
      />
      <MediaControl
        onPress={handlePlay}
        position="absolute"
        top="50%"
        right="50%"
        transform={[{ translateY: -24 }, { translateX: 24 }]}
        zIndex={1}
      >
        {isBuffering ? (
          <Spinner size="large" color="$cross-white" />
        ) : (
          <HeroIcon icon={playAndPauseIcon} size={32} color="$cross-white" />
        )}
      </MediaControl>
      <YStack
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        paddingHorizontal="$spacing-inset-xs"
        paddingVertical="$spacing-inset-nano"
        gap="$spacing-inset-nano"
      >
        <XStack justifyContent="space-between" alignItems="center">
          <View>
            <Text
              color="$cross-white"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
            >
              {formatTime}
            </Text>
          </View>
          <MediaControl onPress={() => setShowPlaybackRateSelector(true)}>
            <HeroIcon icon={['fal', 'cog']} size={24} color="$cross-white" />
          </MediaControl>
        </XStack>
        <View>
          <RangeInput
            rangeColor={rangeColor}
            foregroundColor="rgba(207, 207, 207, 0.40)"
            value={currentTime}
            min={0}
            max={duration}
            step={1}
            onValueChange={handleProgressChange}
          />
        </View>
      </YStack>
      <PlaybackRateSelector
        open={showPlaybackRateSelector}
        onClose={() => setShowPlaybackRateSelector(false)}
        playbackRateOptions={PLAYBACK_RATE_OPTIONS}
        onPlaybackRateChange={handlePlaybackRateChange}
        currentPlaybackRate={playbackRate}
      />
    </View>
  )
}

const MediaControl = styled(View, {
  role: 'button',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  padding: '$spacing-inset-xxs',
})
